// Shared by the Top 10 and History pages: which efforts exist, how to rank them and how to write their values.

// higherIsBetter: speeds rank high-to-low; std dev (consistency) and finish times rank low-to-high.
// distanceKm marks a best-time metric: the value is in seconds, so speed and pace follow from the distance.
export const METRICS = [
  { field: 'Best1MinSpeed',         label: 'Best 1 min',  unit: 'km/h', higherIsBetter: true,  pace: true },
  { field: 'Best5MinSpeed',         label: 'Best 5 min',  unit: 'km/h', higherIsBetter: true,  pace: true },
  { field: 'Best10MinSpeed',        label: 'Best 10 min', unit: 'km/h', higherIsBetter: true,  pace: true },
  { field: 'Best20MinSpeed',        label: 'Best 20 min', unit: 'km/h', higherIsBetter: true,  pace: true },
  { field: 'Best30MinSpeed',        label: 'Best 30 min', unit: 'km/h', higherIsBetter: true,  pace: true },
  { field: 'Best60MinSpeed',        label: 'Best 60 min', unit: 'km/h', higherIsBetter: true,  pace: true },
  { field: 'Best5KTime',            label: 'Best 5K',            unit: 'Time', higherIsBetter: false, distanceKm: 5 },
  { field: 'Best10KTime',           label: 'Best 10K',           unit: 'Time', higherIsBetter: false, distanceKm: 10 },
  { field: 'Best10MilesTime',       label: 'Best 10 miles',      unit: 'Time', higherIsBetter: false, distanceKm: 16.09344 },
  { field: 'BestHalfMarathonTime',  label: 'Best half marathon', unit: 'Time', higherIsBetter: false, distanceKm: 21.0975 },
  { field: 'Consistent20MinStdDev', label: 'Most consistent 20 min', unit: 'km/h std dev', higherIsBetter: false, pace: false, note: 'Lower = more consistent' },
  { field: 'Consistent30MinStdDev', label: 'Most consistent 30 min', unit: 'km/h std dev', higherIsBetter: false, pace: false, note: 'Lower = more consistent' },
];

export const TOP_N = 10;
export const ALL = 'All';
export const DEFAULT_SPORT = 'Run';

export function formatPace(speedKmh) {
  const minPerKm = 60 / speedKmh;
  const mins = Math.floor(minPerKm);
  const secs = Math.round((minPerKm - mins) * 60);
  return secs === 60 ? `${mins + 1}:00` : `${mins}:${String(secs).padStart(2, '0')}`;
}

export function formatDuration(totalSeconds) {
  const s = Math.round(totalSeconds);
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const sec = s % 60;
  return h > 0
    ? `${h}:${String(m).padStart(2, '0')}:${String(sec).padStart(2, '0')}`
    : `${m}:${String(sec).padStart(2, '0')}`;
}

// What to show for one value: the main text, the pace (/km) and the speed (km/h) when the metric has them
// (std dev has neither). For a best time the speed follows from the distance.
export function cellsFor(metric, value) {
  if (metric.distanceKm) {
    const speed = (metric.distanceKm / value) * 3600;
    return { main: formatDuration(value), pace: formatPace(speed), speed };
  }
  return { main: value.toFixed(2), pace: metric.pace ? formatPace(value) : null, speed: metric.pace ? value : null };
}

// 'Best 5K' -> '5K', 'Best half marathon' -> 'Half marathon': the prefix for column headers
export function shortLabel(metric) {
  const s = metric.label.replace(/^Best /, '');
  return s.charAt(0).toUpperCase() + s.slice(1);
}

export function formatDate(iso) {
  return iso ? new Date(iso).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) : '';
}

// ['All', 'Ride', 'Run', ...] for the sport buttons
export function sportsOf(history) {
  const found = Array.from(new Set((history || []).map((r) => r.SportType).filter(Boolean))).sort();
  return [ALL, ...found];
}

export function defaultSport(sports) {
  return sports.includes(DEFAULT_SPORT) ? DEFAULT_SPORT : ALL;
}

// The best `n` activities for a metric (0 = metric not available for that activity, so those are left out)
export function topRows(history, metric, sport, n = TOP_N) {
  const rows = (history || [])
    .filter((r) => sport === ALL || r.SportType === sport)
    .map((r) => ({ ...r, value: parseFloat(r[metric.field]) }))
    .filter((r) => r.value > 0);
  rows.sort((a, b) => (metric.higherIsBetter ? b.value - a.value : a.value - b.value));
  return rows.slice(0, n);
}
